import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { of } from 'rxjs';
import { Observable } from 'rxjs';
import { switchMap, map } from 'rxjs/operators';
import { ApiService, Carrello, Indirizzo, ProdottoCarrello } from './api.service';
import { HeaderService } from './header.service';
import { StorageService } from './storage.service';

@Injectable({providedIn: 'root'})
export class CarrelloService {
  constructor(
    private storage: StorageService,
    private apiService: ApiService,
    private translate: TranslateService,
    private header: HeaderService
  ) { }

  getCarrello(): Observable<Carrello> {
    if (this.storage.isBrowser) {
      const carrelloInSessione: Carrello = this.storage.getLocal('carrello-stem');
      return carrelloInSessione ? of(carrelloInSessione) : this.aggiornaCarrello();
    } else {
      return of(new Carrello());
    }
  }

  aggiornaCarrello(): Observable<Carrello> {
    const idCarrello = this.storage.getLocal('carrello-stem') ? this.storage.getLocal('carrello-stem').idCarrello : null;
    const language = this.translate.currentLang.toUpperCase();
    return this.apiService.getCarrello({language, idCarrello}).pipe(map(({carrello}) => {
        if (!carrello.prodotti) { carrello.prodotti = []; }
        this.storage.setLocal('carrello-stem', carrello);
        this.header.aggiornaCarrello.emit();
        return carrello;
    }));
  }

  salvaCarrello(carrello: Carrello): Observable<Carrello> {
    const language = this.translate.currentLang.toUpperCase();
    const utente = this.storage.getLocal('user-stem');
    if (utente) { carrello.idUtente = utente.idUtente; }
    return this.apiService.saveCarrello({language, carrello}).pipe(
      map((res) => {
        if (res.carrello && res.carrello.idCarrello) {
          if (!carrello.prodotti) { carrello.prodotti = []; }
          this.storage.setLocal('carrello-stem', res.carrello);
          this.header.aggiornaCarrello.emit();
        }
        return res.carrello;
      })
    );
  }

  salvaIndirizzo(indirizzo: Indirizzo): Observable<Carrello> {
    const language = this.translate.currentLang.toUpperCase();
    const utente = this.storage.getLocal('user-stem');
    if (utente) { indirizzo.idUtente = utente.idUtente; }
    return this.getCarrello().pipe(switchMap( ({idCarrello}) => {
      indirizzo.idCarrello = idCarrello;
      return this.apiService.saveIndirizzo({language, indirizzo} ).pipe(map(res => res.carrello));
    }));
  }

  salvaProdotto(prodotto: ProdottoCarrello): Observable<Carrello> {
    return this.getCarrello().pipe(
      switchMap( (carrello) => {
        const prodottoEsistente = carrello.prodotti.find(p => p.idScheda === prodotto.idScheda);
        prodottoEsistente ?
          carrello.prodotti.splice(carrello.prodotti.indexOf(prodottoEsistente), 1, prodotto) :
          carrello.prodotti.push(prodotto);
        return this.salvaCarrello(carrello);
      })
    );
  }

  salvaProdotti(prodotti: ProdottoCarrello[]): Observable<Carrello> {
    return this.getCarrello().pipe(
      switchMap( (carrello) => {
        prodotti.forEach( prodotto => {
          const prodottoEsistente = carrello.prodotti.find(p => p.idScheda === prodotto.idScheda);
          prodottoEsistente ?
            carrello.prodotti.splice(carrello.prodotti.indexOf(prodottoEsistente), 1, prodotto) :
            carrello.prodotti.push(prodotto);
        });
        return this.salvaCarrello(carrello);
      })
    );
  }

  rimuoviProdotto(prodotto: ProdottoCarrello): Observable<Carrello> {
    const language = this.translate.currentLang.toUpperCase();
    return this.getCarrello().pipe(
      switchMap( ({idCarrello}) => this.apiService.removeProdottoCarrello({language, idScheda: prodotto.idScheda, idSchedaPadre: prodotto.idSchedaPadre, idPercorso: prodotto.idPercorso, idCarrello })),
      map( ({carrello}) => {
        if (!carrello.prodotti) { carrello.prodotti = []; }
        this.storage.setLocal('carrello-stem', carrello);
        return carrello; }),
    );
  }

  getProdotto(idScheda): Observable<ProdottoCarrello> {
    return this.getCarrello().pipe( map((carrello) => carrello.prodotti.find(p => idScheda === p.idScheda)));
  }

  getProdotti(): Observable<ProdottoCarrello[]> {
    return this.getCarrello().pipe( map((carrello) => carrello.prodotti || []));
  }

  svuotaProdotti(): Observable<Carrello> {
    return this.getCarrello().pipe(
      switchMap( (carrello) => {
        carrello.prodotti = [];
        return this.salvaCarrello(carrello);
      })
    );
  }

  rimuoviCoupon(): Observable<Carrello> {
    return this.getCarrello().pipe(
      switchMap( (carrello) => {
        carrello.codiceCoupon = null;
        return this.salvaCarrello(carrello);
      })
    );
  }

}
